import duckify from '../../tools/duckify';

export const namespace = 'cassie';
export const store = 'shoreS';

export const Types = duckify(namespace, store, [
  'LOAD_IMAGES_IN_RANGE',
  'SET_RANGE_RESULT',
  'LOAD_THUMBNAILS',
  'INSERT_METADATA',
  'ACQUIRE_IMAGE',
  'SET_LOADING',
  'SET_ERROR',
  'RESET',
]);

export const Actions = {
  /**
   * @function loadImagesInRange
   * @description Requests available images within [start, end]. The saga fetches only the
   * portions of the range not already cached (gaps) and merges the results.
   * @param {string} start - ISO date.
   * @param {string} end - ISO date.
   * **Called by:
   * @see {ShoreSPeriodImageChooser}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type LOAD_IMAGES_IN_RANGE.
   */
  loadImagesInRange: (start, end) => ({
    type: Types.LOAD_IMAGES_IN_RANGE,
    payload: { start, end },
  }),

  /**
   * @function setRangeResult
   * @description Merges newly fetched missions/dates into the cache and records the covered ranges.
   * @param {Array} missions - [{ name, shortname, dates: { dayISO: cloud } }].
   * @param {Array<Array<string>>} ranges - Ranges that were just fetched, each a `[start, end]` pair.
   * **Called by:
   * @see {shoreS/saga}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type SET_RANGE_RESULT.
   */
  setRangeResult: (missions, ranges) => ({
    type: Types.SET_RANGE_RESULT,
    payload: { missions, ranges },
  }),

  /**
   * @function loadThumbnails
   * @description Requests GEE thumbnail URLs for the given images (used by the image
   * list, scoped to whatever subset is currently displayed there — not
   * necessarily the full `availableDates` cache).
   * @param {Array<{name: string, date: string}>} images
   * **Called by:
   * @see {ShoreSPeriodImageChooser}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type LOAD_THUMBNAILS.
   */
  loadThumbnails: (images) => ({
    type: Types.LOAD_THUMBNAILS,
    payload: { images },
  }),

  /**
   * @function insertMetadata
   * @description Records one image's thumbnail metadata. Mirrors the `acquisition` duck's
   * `insertMetadata`/`metadata` shape so `ImageTable` can be reused as-is.
   * @param {string} missionName
   * @param {string} date
   * @param {{id: *, thumbnail: string, clouds: number}} metadata
   * **Called by:
   * @see {shoreS/saga}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type INSERT_METADATA.
   */
  insertMetadata: (missionName, date, metadata) => ({
    type: Types.INSERT_METADATA,
    payload: { missionName, date, metadata },
  }),

  /**
   * @function acquireImage
   * @description Loads a single image as a raster layer on the map (band viewer), same
   * end result as the `acquisition` duck's `acquireImage` — but using the
   * Shore-S ROI (`shoreSModel.roiBBox`) and a day-mosaic acquisition (Shore-S
   * dates are day-only, no exact timestamp) instead of the AOI geometry and
   * exact-timestamp acquisition `acquisition`'s version relies on.
   * @param {string} missionName
   * @param {string} date - Day (YYYY-MM-DD).
   * **Called by:
   * @see {ImageChooserCard}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type ACQUIRE_IMAGE.
   */
  acquireImage: (missionName, date) => ({
    type: Types.ACQUIRE_IMAGE,
    payload: { missionName, date },
  }),

  /**
   * @function setLoading
   * @description Toggles the Shore-S slice's loading flag, also clearing any stored error
   * whenever a new fetch starts.
   * @param {boolean} loading
   * **Called by:
   * @see {shoreS/saga}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type SET_LOADING.
   */
  setLoading: (loading) => ({
    type: Types.SET_LOADING,
    payload: { loading },
  }),

  /**
   * @function setError
   * @description Records an error condition in the Shore-S slice so the UI can show an
   * actionable message. Pass null to clear the error.
   * Known values: 'no-roi' (bounding box not available).
   * @param {string|null} error
   * **Called by:
   * @see {shoreS/saga}
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type SET_ERROR.
   */
  setError: (error) => ({
    type: Types.SET_ERROR,
    payload: { error },
  }),

  /**
   * @function reset
   * @description Clears the Shore-S cache (e.g. when leaving the flow).
   * @memberof module:ShoreS
   * @returns {Object} Redux action of type RESET.
   */
  reset: () => ({
    type: Types.RESET,
  }),
};

export default Actions;
